import {
	CAMPAIGN_ROUTING_MAX_CONDITIONS,
	CAMPAIGN_ROUTING_MAX_RULES,
	CAMPAIGN_ROUTING_MAX_VALUES,
	CampaignRoutingCondition,
	CampaignRoutingFallback,
	CampaignRoutingSave,
	CampaignRoutingSettings,
} from "@signalist/shared-types";

import { newCondition, takesValues } from "./campaignRoutingFields";

/** A condition being edited, with a stable key for React. */
export type DraftCondition = CampaignRoutingCondition & { uid: string };

/** A rule being edited: its conditions (AND) and the campaign it assigns to, if one is picked. */
export interface DraftRule {
	uid: string;
	conditions: DraftCondition[];
	campaignUuid: string | null;
}

/** The editor's whole state: the ordered rules and the no-match fallback. */
export interface RoutingDraft {
	rules: DraftRule[];
	fallback: CampaignRoutingFallback;
}

let uidCounter = 0;

/** A key unique within the page, for React lists of rules and conditions. */
export const nextUid = (prefix: string): string => {
	uidCounter += 1;

	return `${prefix}-${uidCounter}`;
};

export const draftCondition = (
	condition: CampaignRoutingCondition = newCondition()
): DraftCondition => ({
	...condition,
	uid: nextUid("condition"),
});

export const emptyRule = (): DraftRule => ({
	uid: nextUid("rule"),
	conditions: [draftCondition()],
	campaignUuid: null,
});

/** The editor's starting state: the server's rules in order and its fallback. */
export const toDraft = (settings: CampaignRoutingSettings | undefined): RoutingDraft => ({
	rules: [...(settings?.rules ?? [])]
		.sort((a, b) => a.position - b.position)
		.map((rule) => ({
			uid: nextUid("rule"),
			conditions: rule.conditions.map((condition) => draftCondition(condition)),
			campaignUuid: rule.campaignUuid,
		})),
	fallback: settings?.fallback ?? "DEFAULT_CAMPAIGN",
});

const toSavedCondition = (condition: DraftCondition): CampaignRoutingCondition => {
	if (condition.operator === "BETWEEN") {
		return {
			field: condition.field,
			operator: condition.operator,
			from: condition.from ?? null,
			to: condition.to ?? null,
		};
	}

	return {
		field: condition.field,
		operator: condition.operator,
		values: takesValues(condition.operator)
			? (condition.values ?? []).map((value) => value.trim()).filter(Boolean)
			: [],
	};
};

/** The PUT body: the whole ordered set, positions taken from the order shown. */
export const toSaveBody = (draft: RoutingDraft): CampaignRoutingSave => ({
	fallback: draft.fallback,
	rules: draft.rules.map((rule) => ({
		conditions: rule.conditions.map(toSavedCondition),
		campaignUuid: rule.campaignUuid ?? "",
	})),
});

/** The rules with one moved up (-1) or down (+1); a move past either end changes nothing. */
export const moveRule = (rules: DraftRule[], index: number, delta: -1 | 1): DraftRule[] => {
	const target = index + delta;

	if (index < 0 || index >= rules.length || target < 0 || target >= rules.length) {
		return rules;
	}

	const moved = [...rules];

	[moved[index], moved[target]] = [moved[target], moved[index]];

	return moved;
};

/** What keeps a rule from being saved, in the order the user should fix it. */
export type RuleProblem =
	| "noCampaign"
	| "tooManyConditions"
	| "noValues"
	| "tooManyValues"
	| "noRange"
	| "badRange";

const conditionProblem = (condition: DraftCondition): RuleProblem | null => {
	if (condition.operator === "BETWEEN") {
		const from = condition.from ?? null;
		const to = condition.to ?? null;

		if (from === null && to === null) {
			return "noRange";
		}

		return from !== null && to !== null && from > to ? "badRange" : null;
	}

	const values = (condition.values ?? []).filter((value) => value.trim());

	if (takesValues(condition.operator) && values.length === 0) {
		return "noValues";
	}

	return values.length > CAMPAIGN_ROUTING_MAX_VALUES ? "tooManyValues" : null;
};

/** The first problem of a rule, or null when the API will take it. */
export const ruleProblemOf = (rule: DraftRule): RuleProblem | null => {
	if (!rule.campaignUuid) {
		return "noCampaign";
	}

	if (rule.conditions.length === 0) {
		return "noValues";
	}

	if (rule.conditions.length > CAMPAIGN_ROUTING_MAX_CONDITIONS) {
		return "tooManyConditions";
	}

	for (const condition of rule.conditions) {
		const problem = conditionProblem(condition);

		if (problem) {
			return problem;
		}
	}

	return null;
};

/** Whether the draft can be saved: every rule complete and within the limits. */
export const isDraftSaveable = (draft: RoutingDraft): boolean =>
	draft.rules.length <= CAMPAIGN_ROUTING_MAX_RULES &&
	draft.rules.every((rule) => ruleProblemOf(rule) === null);

export const canAddRule = (draft: RoutingDraft): boolean =>
	draft.rules.length < CAMPAIGN_ROUTING_MAX_RULES;

export const canAddCondition = (rule: DraftRule): boolean =>
	rule.conditions.length < CAMPAIGN_ROUTING_MAX_CONDITIONS;
